import { CheckCircle2, History, LoaderCircle, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { auditAuthorLabel, auditAuthorTitle } from "@/features/admin/audit-author";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import {
  apiClient,
  type ClassificationRevertResult,
  type MentionEditEntry,
  type MentionEditor
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { Pager } from "@/shared/components/Pager";
import { SearchSelect } from "@/shared/components/SearchSelect";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { DateField } from "@/shared/components/DateField";
import { toMention, type Mention } from "@/shared/types/mention";
import { formatInteger } from "@/shared/utils/format";
import { periodoDaConsulta } from "@/shared/utils/instant-period";
import {
  CLASSIFICATION_EDIT_KINDS,
  CLASSIFICATION_EDIT_KIND_LABELS,
  type ClassificationEditKind
} from "@/shared/vocabulary/classification-edit-kinds";
import { BulkEditsTab } from "./BulkEditsTab";
import { describeEdit, describeRevert, editFieldLabel } from "./edit-diff";
import { RevertVersionDialog } from "./RevertVersionDialog";

const PAGE_SIZE = 50;

export type HistoryTab = "changes" | "batches";

/** Os filtros da tela. As datas são `AAAA-MM-DD` do `<input type=date>`,
 *  no fuso de quem olha — a conversão pra instante vai em
 *  `periodoDaConsulta`. */
type Filters = {
  /** Só as linhas de um lote de edição (`?lote=N`, vindo da aba Lotes). */
  bulk_edit_id: number | null;
  changed_by: string;
  kind: string;
  from: string;
  to: string;
  page: number;
};

const NO_FILTER: Filters = { bulk_edit_id: null, changed_by: "", kind: "", from: "", to: "", page: 1 };

function editorLabel(editor: MentionEditor): string {
  return editor.name || editor.id;
}

function formatWhen(iso: string): string {
  const when = parseServerTime(iso);
  return when === null ? iso : new Date(when).toLocaleString("pt-BR");
}

/** O aviso depois de uma volta de versão: o que aconteceu e o que não voltou. */
function revertNotice(title: string, result: ClassificationRevertResult): string {
  const done = Object.keys(result.diff).length
    ? `“${title}” voltou à versão escolhida.`
    : `“${title}” já estava nessa versão — a classificação não mudou.`;
  if (!result.skipped.length) return done;
  return `${done} Ficou como estava: ${result.skipped.map(editFieldLabel).join(", ")}.`;
}

/** Linha anterior à coluna `kind` é edição manual. */
function kindLabel(kind: MentionEditEntry["kind"]): string {
  return CLASSIFICATION_EDIT_KIND_LABELS[(kind ?? "manual") as ClassificationEditKind] ?? String(kind);
}

/**
 * O histórico de edições da empresa (2026-10-18): toda correção de
 * classificação — manual, em lote, por reprocessamento de IA, revisão,
 * descarte e vínculo manual —, da mais recente pra mais antiga, com
 * filtro por pessoa, origem e período e um jeito de abrir a menção.
 *
 * `changed_at` é carimbo de VERDADE (o instante em que alguém salvou), por
 * isso converte fuso (`parseServerTime`), ao contrário da data de
 * publicação. Falha de CARREGAMENTO com dados na tela mantém a lista e
 * avisa; falha de AÇÃO (abrir a menção) não troca a página.
 */
export function EditHistoryPage({
  token,
  companyId,
  openMention,
  mentionChange,
  tab: initialTab = "changes",
  bulkEditId: initialBulkEditId = null,
  onViewChange
}: {
  token: string;
  companyId: string;
  openMention: (mention: Mention) => void;
  mentionChange: number;
  /** A aba e o filtro de lote com que a tela abre (a URL: `?aba=lotes`,
   *  `?lote=N`). */
  tab?: HistoryTab;
  bulkEditId?: number | null;
  /** Avisa a rota quando a aba ou o filtro de lote mudam, pra a URL
   *  acompanhar. */
  onViewChange?: (view: { tab: HistoryTab; bulkEditId: number | null }) => void;
}) {
  const [tab, setTab] = useState<HistoryTab>(initialTab);
  const [filters, setFilters] = useState<Filters>({ ...NO_FILTER, bulk_edit_id: initialBulkEditId });
  const [items, setItems] = useState<MentionEditEntry[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [editors, setEditors] = useState<MentionEditor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // A linha de onde se abriu "Voltar a esta versão", e o aviso do desfecho.
  const [reverting, setReverting] = useState<MentionEditEntry | null>(null);
  const [revertDone, setRevertDone] = useState<string | null>(null);
  // Descarte por CARGA: a resposta de uma consulta antiga não vence a atual.
  const loadSeq = useRef(0);
  const controller = useRef<AbortController | null>(null);

  // O calendário já desabilita dia fora da faixa (`min`/`max`); a guarda
  // fica por segurança, pra um estado torto nunca virar consulta.
  const invertedPeriod = filters.from !== "" && filters.to !== "" && filters.from > filters.to;
  const filtering =
    filters.bulk_edit_id !== null ||
    filters.changed_by !== "" || filters.kind !== "" || filters.from !== "" || filters.to !== "";

  const showTab = (next: HistoryTab, bulkEditId: number | null) => {
    setTab(next);
    onViewChange?.({ tab: next, bulkEditId });
  };

  /** Filtra as alterações por um lote (ou tira o filtro, com `null`). */
  const filterByBatch = (bulkEditId: number | null) => {
    setFilters(current => ({ ...current, bulk_edit_id: bulkEditId, page: 1 }));
    showTab("changes", bulkEditId);
  };

  const load = useCallback(async () => {
    if (tab !== "changes" || invertedPeriod) return;
    const seq = ++loadSeq.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setLoading(true);
    try {
      const result = await apiClient.listClassificationHistory(token, {
        changed_by: filters.changed_by || undefined,
        kind: filters.kind || undefined,
        bulk_edit_id: filters.bulk_edit_id ?? undefined,
        ...periodoDaConsulta(filters.from, filters.to),
        page: filters.page,
        size: PAGE_SIZE
      });
      if (seq !== loadSeq.current) return;
      setItems(result.items);
      setTotal(result.total);
      setError(null);
    } catch (failure) {
      if (seq !== loadSeq.current || abort.signal.aborted) return;
      setError(errorMessage(failure, "Não foi possível carregar o histórico."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [token, filters, invertedPeriod, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => controller.current?.abort(), []);

  // As opções de "Quem": uma vez, na primeira vez que a aba de alterações
  // aparece; falha vira só "Todos".
  const editorsRequested = useRef(false);
  useEffect(() => {
    if (tab !== "changes" || editorsRequested.current) return;
    editorsRequested.current = true;
    apiClient
      .listMentionEditors(token)
      .then(setEditors)
      .catch(() => {});
  }, [token, tab]);

  // O drawer mudou uma menção: a lista se refaz. Pula a montagem.
  const seenMentionChange = useRef(mentionChange);
  useEffect(() => {
    if (seenMentionChange.current === mentionChange) return;
    seenMentionChange.current = mentionChange;
    void load();
  }, [mentionChange, load]);

  const openEntry = async (entry: MentionEditEntry) => {
    setActionError(null);
    try {
      openMention(toMention(await apiClient.mentionDetail(token, entry.mention.id)));
    } catch (failure) {
      setActionError(errorMessage(failure, "Não foi possível abrir a menção."));
    }
  };

  return (
    <div className="uploads-page edit-history-page" data-company={companyId}>
      {/* A mesma casca da Lixeira e de Downloads (um card com título e
          intro), e não o `ajustes-panel`: a folha de Ajustes carimba
          "Central de ajustes" no cabeçalho por CSS, e esta tela não é
          de Ajustes. */}
      <section className="card upload-history-card">
        <div className="card-title">
          <h3>
            <History size={17} />
            Histórico de edições
            {total !== null ? ` · ${formatInteger(total)} registro${total === 1 ? "" : "s"}` : ""}
          </h3>
          {loading && <LoaderCircle className="spin" size={16} />}
        </div>
        <p className="downloads-intro">
          Quem corrigiu a classificação das menções da empresa, e quando: correção manual, em
          lote, reprocessamento por IA, revisão, descarte e vínculo manual. Só Administrador ou
          acima vê esta tela.
        </p>

        <div className="mode-toggle downloads-tabs" role="tablist" aria-label="Histórico de edições">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "changes"}
            className={tab === "changes" ? "active" : ""}
            onClick={() => showTab("changes", filters.bulk_edit_id)}
          >
            Alterações
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "batches"}
            className={tab === "batches" ? "active" : ""}
            onClick={() => showTab("batches", null)}
          >
            Lotes
          </button>
        </div>

        {tab === "batches" && (
          <BulkEditsTab token={token} onShowChanges={filterByBatch} />
        )}

        {tab === "changes" && (
          <>
      {/* A barra de filtros fala a língua das outras telas de empresa: a
          escolha com busca (`SearchSelect`), o seletor em pílula da barra
          de menções (`ToolbarSelect`) e o calendário da plataforma
          (`DateField`), nunca o `<select>` e o `<input type=date>` nativos
          — a primeira versão usava os dois e destoava do resto do site. */}
      <div className="edit-history-filters">
        <div className="audit-filter-pick">
          <SearchSelect
            label="Quem"
            value={filters.changed_by}
            options={editors.map(editor => ({ value: editor.id, label: editorLabel(editor) }))}
            allLabel="Todos"
            onChange={id => setFilters({ ...filters, changed_by: id, page: 1 })}
          />
        </div>
        <ToolbarSelect
          prefix="Origem"
          ariaLabel="Origem"
          value={filters.kind}
          options={[
            { value: "", label: "Todas" },
            ...CLASSIFICATION_EDIT_KINDS.map(kind => ({ value: kind, label: CLASSIFICATION_EDIT_KIND_LABELS[kind] }))
          ]}
          onChange={kind => setFilters({ ...filters, kind, page: 1 })}
        />
        <div className="edit-history-period">
          <span className="toolbar-select-label">De</span>
          <DateField
            ariaLabel="De"
            value={filters.from}
            max={filters.to || undefined}
            onValue={from => setFilters({ ...filters, from, page: 1 })}
          />
          <span className="toolbar-select-label">até</span>
          <DateField
            ariaLabel="Até"
            value={filters.to}
            min={filters.from || undefined}
            align="right"
            onValue={to => setFilters({ ...filters, to, page: 1 })}
          />
        </div>
        {filters.bulk_edit_id !== null && (
          <button
            type="button"
            className="edit-history-chip"
            onClick={() => filterByBatch(null)}
            aria-label={`Tirar o filtro do lote #${filters.bulk_edit_id}`}
          >
            Lote #{filters.bulk_edit_id}
            <X size={12} aria-hidden="true" />
          </button>
        )}
        {filtering && (
          <button
            type="button"
            className="ghost-button"
            onClick={() => {
              setFilters(NO_FILTER);
              onViewChange?.({ tab, bulkEditId: null });
            }}
          >
            <SlidersHorizontal size={14} aria-hidden="true" />
            Limpar filtros
          </button>
        )}
      </div>
      {invertedPeriod && (
        <p className="form-error" role="alert">
          A data inicial é depois da final — nenhuma edição cabe nesse período.
        </p>
      )}

      {error && (
        <ApiState kind="error" onRetry={() => void load()}>
          <span role="alert">{error}</span>
        </ApiState>
      )}
      {actionError && (
        <p className="form-error" role="alert">
          {actionError}
        </p>
      )}
      {revertDone && (
        <div className="edit-history-notice is-done" role="status" aria-live="polite">
          <p>
            <CheckCircle2 size={16} aria-hidden="true" />
            {revertDone}
            <button
              type="button"
              className="icon-button"
              onClick={() => setRevertDone(null)}
              aria-label="Fechar aviso"
            >
              <X size={14} />
            </button>
          </p>
        </div>
      )}

      <div className="edit-history-list">
        {items !== null && items.length === 0 ? (
          <p className="copiloto-empty-list">Nenhuma edição registrada com esse recorte.</p>
        ) : (
          items !== null && (
            <table className="ai-admin-table audit-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Menção</th>
                  <th>Origem</th>
                  <th>Quem</th>
                  <th>Mudança</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map(entry => {
                  const when = parseServerTime(entry.changed_at);
                  const lines = describeEdit(entry);
                  const revert = describeRevert(entry, formatWhen);
                  const meta = [entry.mention.brand_name, entry.mention.channel]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    // A linha inteira abre a menção (2026-10-19). O botão
                    // "Abrir" fica pra teclado e leitor de tela; quem
                    // arrasta pra copiar um trecho não abre nada.
                    <tr
                      key={entry.id}
                      className="edit-history-row"
                      onClick={() => {
                        if (window.getSelection()?.toString()) return;
                        void openEntry(entry);
                      }}
                    >
                      <td>{when === null ? entry.changed_at : new Date(when).toLocaleString("pt-BR")}</td>
                      <td>
                        {entry.mention.title || "(sem título)"}
                        {meta && (
                          <>
                            <br />
                            <small>{meta}</small>
                          </>
                        )}
                      </td>
                      <td>
                        {kindLabel(entry.kind)}
                        {entry.bulk_edit_id != null && (
                          <>
                            <br />
                            <button
                              type="button"
                              className="text-button edit-history-batch-link"
                              onClick={event => {
                                // Senão o clique sobe pra linha e abre a menção.
                                event.stopPropagation();
                                filterByBatch(entry.bulk_edit_id ?? null);
                              }}
                            >
                              {entry.bulk_edit_undo_of != null
                                ? `Desfazer do lote #${entry.bulk_edit_undo_of}`
                                : `Lote #${entry.bulk_edit_id}`}
                            </button>
                          </>
                        )}
                      </td>
                      <td title={auditAuthorTitle(entry)}>{auditAuthorLabel(entry)}</td>
                      <td className="audit-fields">
                        {revert && <div className="edit-history-revert-note">{revert}</div>}
                        {lines.map(line => (
                          <div key={line.field}>
                            <strong>{line.label}:</strong> {line.text}
                          </div>
                        ))}
                      </td>
                      <td>
                        <div className="edit-history-row-actions">
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={event => {
                              // Senão o clique sobe pra linha e abre duas vezes.
                              event.stopPropagation();
                              void openEntry(entry);
                            }}
                            aria-label={`Abrir ${entry.mention.title || "menção " + entry.mention.id}`}
                          >
                            Abrir
                          </button>
                          {/* Voltar a menção a ESTA versão (2026-10-31). O
                              diálogo mostra a trilha da menção inteira e
                              deixa trocar a versão antes de confirmar. */}
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={event => {
                              event.stopPropagation();
                              setRevertDone(null);
                              setReverting(entry);
                            }}
                            aria-label={`Voltar ${entry.mention.title || "menção " + entry.mention.id} a uma versão`}
                            title="Voltar a menção a esta versão"
                          >
                            <RotateCcw size={14} aria-hidden="true" />
                            Voltar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {items === null && !error && <ApiState kind="loading">Carregando…</ApiState>}

        {total !== null && (
          <Pager
            page={filters.page}
            size={PAGE_SIZE}
            count={{ total }}
            onPage={next => setFilters({ ...filters, page: next })}
          />
        )}
      </div>
          </>
        )}
      </section>
      {reverting && (
        <RevertVersionDialog
          token={token}
          mentionId={reverting.mention.id}
          mentionTitle={reverting.mention.title || "(sem título)"}
          initialEditId={reverting.id}
          onClose={() => setReverting(null)}
          onReverted={result => {
            setRevertDone(revertNotice(reverting.mention.title || "(sem título)", result));
            setReverting(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
